/**
 * People faces: one way to colour a person and one way to letter them.
 *
 * The dashboard's own copy of the presence rule the AppNotes SDK uses (FNV-1a
 * of the stable id, modulo eight slots), so a person reads the same in the
 * review panel and here. The palette lives in index.css as `--p0` to `--p7`.
 */

/** Number of presence colour slots. */
export const SLOTS = 8;

function fnv1a(seed: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** The colour slot for a stable id: FNV-1a(id) % 8. */
export const slotOf = (id: string): number => fnv1a(id) % SLOTS;

const firstLetter = (word: string | undefined): string =>
  (Array.from(word ?? "")[0] ?? "").toUpperCase();

const words = (name: string | null | undefined): string[] =>
  (name ?? "").trim().split(/\s+/u).filter(Boolean);

/** Two initials: first letter of the first and last word; one word gives one. */
export function initials(name: string | null | undefined): string {
  const parts = words(name);
  if (parts.length === 0) return "?";
  return parts.length === 1
    ? firstLetter(parts[0])
    : firstLetter(parts[0]) + firstLetter(parts.at(-1));
}

/** One initial: the first letter of the first word. */
export function initial(name: string | null | undefined): string {
  return firstLetter(words(name)[0]) || "?";
}

/** Client mark drawn for an agent author. */
export type AgentGlyph = "claude" | "codex" | "cursor" | "terminal";

/** Picks the agent glyph from the client or key name. */
export function agentGlyph(name: string | null | undefined): AgentGlyph {
  const value = (name ?? "").trim().toLowerCase();
  if (value.includes("claude")) return "claude";
  if (value.includes("codex")) return "codex";
  if (value.includes("cursor")) return "cursor";
  return "terminal";
}

/** First word of a name, for "Reply to Maya". */
export function firstName(name: string): string {
  return words(name)[0] ?? name;
}

/**
 * How one author is drawn. Members carry the id their colour slot comes from;
 * guests have no account id, so they draw neutral; agents draw their client.
 */
export type Person =
  | { readonly kind: "member"; readonly id: string; readonly name: string }
  | { readonly kind: "guest"; readonly name: string }
  | {
      readonly kind: "agent";
      readonly name: string;
      readonly glyph: AgentGlyph;
    };
